import React, { useCallback, useEffect, useRef, useState } from 'react';
import {Viewer} from './Viewer-Factory/Viewer';
import {useViewerStore} from "../../../../store/store";
import {useNavigate} from "react-router-dom";
import {useCurrentUser} from "../../../../contexts/UserContext";
import moduleServices from "../../../../services/moduleService";
import {getDesignVersionList} from "../../../../services/designVersionService";
import siteService from "../../../../services/siteService";
import getStateCodeFromStateName from "../../../../services/stateCodeService";
import {checkAndSetSetbackOverrideFlag, fetchSetbackInfo} from "./Controllers/PathwayControl";
import {LoadModelJson} from "./Controllers/Loader";
import { addModulesToAllRoofsMaxFill } from './Controllers/ModuleControl';
import {getSite} from "../../../../hooks/useLoadOriginalSite";
import {IoIosLock} from "react-icons/io";
import LockedDesignValidation from "../Modal/Modal-Content/LockedDesignValidation";
import {logger} from "../../../../services/loggingService";
import masterDataService from "../../../../services/masterDataService";
import {getPathnameIncludes} from "../../../../helpers/getPathname";
import {LAYER_OPTIONS} from "../../../../Constants";
import {populateRoofSquareData} from './Controllers/RoofSquareControl';
import {setDeactivateRoofArrInStoreFromDesignData} from "./Controllers/Common";
import {clickButtonWithRef, setPathname, toggleLayer} from '../../../../helpers/storeHelpers';
import {BuildingDesignData, ModuleListAPIResponseItem, ModuleTypeOptionData} from '../../../../@types/types';
import {
    dismissLightReachPitchWarningIfShowing,
    syncLightReachSitePitchWarning,
} from "../../../../helpers/lightReachWarning";
import { pickCustomerSiteRestrictions } from "../../../../helpers/customerSiteRestrictions";
import { applySunPositionVersionFromDesign } from "../../../../helpers/sunPositionVersion";
import {AlertType, EditorControlMode, HideObjectType, SelectType, TabName} from '../../../../@types/enums';
import packingSlipService from '../../../../services/packingSlipService';
import { EditorDashboard } from '../oneBUTTONDashboard/EditorDashboard';
import {
    captureDefaultLayoutSnapshotIfNeeded,
    isSiteModelReadyForSnapshot,
    isValidSnapshotDataUrl,
} from '../../../../helpers/layoutSnapshotCapture';
import {
    applyPciDesignOptionToViewer,
    applyPciIncludedModulesToViewer,
    applyPciModuleSelectionHighlight,
    applyPciRoofSelectionHighlight,
    applyPciTreeGhostSelection,
    applyPciLossesVendor,
    captureIncludedModulesLayout,
    capturePciMaxfillSnapshot,
    capturePciMaxfillStrategies,
    collectPciSiteRoofs,
    ensurePciTreeIdLabels,
    isPciShadeReady,
    pciRoofsHaveSquares,
    PCI_ONEDRAW_APPLY_DESIGN_DONE_MSG,
    PCI_ONEDRAW_APPLY_DESIGN_MSG,
    PCI_ONEDRAW_CAPTURE_MODULES_MSG,
    PCI_ONEDRAW_MAXFILL_MSG,
    PCI_ONEDRAW_MAXFILL_RESULT_MSG,
    PCI_ONEDRAW_MODULE_SELECTION_MSG,
    PCI_ONEDRAW_MODULES_LAYOUT_MSG,
    PCI_ONEDRAW_ROOF_SELECTION_MSG,
    PCI_ONEDRAW_SIMULATE_OPTIONS_MSG,
    PCI_ONEDRAW_SIMULATE_RESULTS_MSG,
    PCI_ONEDRAW_TREE_GHOST_MSG,
    PCI_ONEDRAW_LOSSES_VENDOR_MSG,
    PCI_ONEDRAW_3D_VIEW_MSG,
    PCI_ONEDRAW_VIEWPORT_RESIZE_MSG,
    postPciSiteLoadErrorToParent,
    postPciSiteRoofsToParent,
    fitPciCameraToBuilding,
    preparePci3dView,
    simulatePciOptionsInViewer,
    type PciSimulateOptionJob,
} from '../../../../pre-contract/lib/preContractEmbedBridge';
import type { SavedRoofModulesLayout } from '../../../../@types/types';
import {
    consumePreContractOpenDesign,
    PCI_PRECONTRACT_READY_TO_FINALIZE,
    peekPreContractAutoFinalize,
    peekPreContractOpenDesign,
    shouldOpenCanvasOnConfigureLoad,
} from '../../../../pre-contract/lib/preContractLaunch';
import {
    DEFAULT_SITE_VIEWER_MESSAGE,
    SITE_VIEWER_UNAVAILABLE_TITLE,
    deferSiteViewerCalculationsOnLoad,
} from '../../../../helpers/siteViewer3dAccess';
import { canvasContainerRef } from './canvasRef';
import OneDrawDebuggerGUI from './OneDrawDebuggerGUI';
import EnergyCommunityNotice, { opportunityLikelyQualifiesAsEnergyCommunity} from './EnergyCommunityNotice';
import { isOneDrawDebugMode } from './Controllers/DebugDraw';
import { loadBatchState, saveBatchState } from '../../../../services/batchAIService';
import { formatSiteLoadErrorMessage, OPPORTUNITY_NOT_AVAILABLE_MESSAGE } from '../../../../helpers/siteLoadErrorMessage';
import { ViewerLoadingOverlays } from './ViewerLoadingOverlays';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { triggerV3AiDesignsPanel } from '../oneBUTTONDashboard/sections/dashboardV3AiDesignsTrigger';


let viewerInstance: Viewer;
/** Set true during tree-cutting reprocess so we don't reload on WebGL context lost (heavy GPU load can trigger it). */

const ThreeDViewer: React.FC = () => {

    const {user, hasCapability} = useCurrentUser();
    const navigate = useNavigate();
    const [showEnergyCommunityNotice, setShowEnergyCommunityNotice] = useState(false);

    const {
        sceneViewer,
        pathname,
        layerVisibilityState,
        isGhostObstructionUpdated,
        isHideAllGhostTrees,
        designList,
        selectedDesignIndex,
        loadingCompleted,
        accessoryBuildingList,
        buildingWise33PercentRuleModuleCountList,
        initialLoadLoadingContextRef,
        isNonFinalizedSavedDesign,
        roofPitchOverrideArr,
        financePartnerDetails,
        isDesignLoading,
        roofInfoArr,
        isViewportOnlyEmbed,
        siteLoadingError,
        roofSquareTableData,
        isDashboardOpen,
    } = useViewerStore();


    const [isDesignFetching, setIsDesignFetching] = useState(true);
    const roofSquarePopulateKeyRef = useRef<string>('');
    const isDebugModeEnabled = isOneDrawDebugMode();
    const reportSiteLoadError = useCallback((message: string) => {
        const viewportOnly = useViewerStore.getState().isViewportOnlyEmbed;
        useViewerStore.setState({
            loadingCompleted: true,
            refreshingShade: false,
            siteLoadingError: message,
            // Keep canvas visible in viewport-only embeds (no dashboard chrome).
            isDashboardOpen: viewportOnly ? false : true,
        });
        if (viewportOnly) {
            postPciSiteLoadErrorToParent(message);
        }
    }, []);
    const navigateToNextBatchSite = useCallback((nextRec: { salesforceId: string; designNo: number; versionNo: number }) => {
        sessionStorage.setItem('batchDesignNo', String(nextRec.designNo));
        sessionStorage.setItem('batchVersionNo', String(nextRec.versionNo));
        sessionStorage.setItem('batchSiteStartTime', String(Date.now()));

        // Ensure no viewer state leaks between consecutive /configure -> /configure batch hops.
        try {
            viewerInstance?.dispose?.();
        } catch {
            // ignore dispose failures before hard navigation
        }

        window.location.assign(`/configure?sfid=${nextRec.salesforceId}&batch=true`);
    }, []);

    /**
     * Capture the very first layout that oneDRAW loads (before any max-fill or AI changes)
     * so we can always restore back to that exact default later.
     * Only runs after the site model is drawn — early captures produced blank images.
     */
    useEffect(() => {
        if (!sceneViewer || !isSiteModelReadyForSnapshot(sceneViewer)) return;
        const currentSnapshots = (useViewerStore.getState() as any)?.layoutSnapshotsByKey || {};
        if (isValidSnapshotDataUrl(currentSnapshots.defaultInitial)) return;
        captureDefaultLayoutSnapshotIfNeeded(sceneViewer);
    }, [sceneViewer, loadingCompleted]);

    /** Last parent selection — re-applied after roof republish so polls don't wipe toggles. */
    const pciRoofSelectionRef = useRef<number[] | null>(null);
    const pciModuleSelectionRef = useRef<string[] | null>(null);
    const pciTreeGhostRef = useRef<string[] | null>(null);
    const pciCameraFittedRef = useRef(false);
    const pciResizeFitCountRef = useRef(0);

    /** Pre-contract parent: stop spinning when Aurora/EagleView data is missing. */
    useEffect(() => {
        if (!isViewportOnlyEmbed) return;
        const loadError = siteLoadingError?.trim();
        if (!loadError) return;
        postPciSiteLoadErrorToParent(loadError);
    }, [isViewportOnlyEmbed, siteLoadingError]);

    /** Pre-contract viewport embed: publish real roofs + module grids; retry until roofs post. */
    useEffect(() => {
        if (!isViewportOnlyEmbed || !sceneViewer) return undefined;
        if (siteLoadingError?.trim()) {
            postPciSiteLoadErrorToParent(siteLoadingError);
            return undefined;
        }

        let cancelled = false;
        let lastModuleCount = -1;
        let attempts = 0;
        let postedRoofs = false;
        let postedSquares = false;
        let shadeReadyPosts = 0;
        let maxFillAttempted = false;
        let timer = 0;

        const reapplyParentSelection = () => {
            if (pciRoofSelectionRef.current) {
                applyPciRoofSelectionHighlight(sceneViewer, pciRoofSelectionRef.current);
            } else {
                const roofs = collectPciSiteRoofs(sceneViewer);
                if (roofs.length) {
                    applyPciRoofSelectionHighlight(
                        sceneViewer,
                        roofs.map((r) => r.roofTagId),
                    );
                }
            }
            if (pciModuleSelectionRef.current) {
                applyPciModuleSelectionHighlight(sceneViewer, pciModuleSelectionRef.current);
            }
            if (pciTreeGhostRef.current) {
                applyPciTreeGhostSelection(sceneViewer, pciTreeGhostRef.current, {
                    refreshShade: false,
                });
            }
        };

        const publish = () => {
            if (cancelled) return;
            try {
                sceneViewer.toggleRoofIDTriangle?.(false);
            } catch {
                /* ignore */
            }
            const roofs = collectPciSiteRoofs(sceneViewer);
            const moduleCount = sceneViewer.moduleMeshArr?.length ?? 0;
            if (
                roofs.length &&
                moduleCount === 0 &&
                useViewerStore.getState().loadingCompleted &&
                !maxFillAttempted
            ) {
                maxFillAttempted = true;
                try {
                    addModulesToAllRoofsMaxFill(sceneViewer, null, 'port');
                } catch {
                    /* ignore */
                }
            }
            if (roofs.length) {
                reapplyParentSelection();
                postPciSiteRoofsToParent(sceneViewer);
                postedRoofs = true;
                if (pciRoofsHaveSquares(roofs)) postedSquares = true;
                if (!pciCameraFittedRef.current) {
                    fitPciCameraToBuilding(sceneViewer, 'top');
                    pciCameraFittedRef.current = true;
                }
            }
            ensurePciTreeIdLabels(sceneViewer);
            lastModuleCount = moduleCount;
            attempts += 1;
        };

        const scheduleNext = () => {
            if (cancelled) return;
            const count = sceneViewer.moduleMeshArr?.length ?? 0;
            const shadeReady = isPciShadeReady(sceneViewer);
            const shouldPublish =
                !postedRoofs ||
                !postedSquares ||
                count !== lastModuleCount ||
                !shadeReady ||
                shadeReadyPosts < 4 ||
                attempts <= 12;
            if (shouldPublish) {
                publish();
                if (shadeReady) shadeReadyPosts += 1;
            }
            if (postedRoofs && postedSquares && shadeReadyPosts >= 4) {
                return;
            }
            if (postedRoofs && attempts >= 120) {
                return;
            }
            const delayMs = attempts < 16 ? 450 : 1500;
            timer = window.setTimeout(scheduleNext, delayMs);
        };

        publish();
        timer = window.setTimeout(scheduleNext, 450);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [isViewportOnlyEmbed, sceneViewer, loadingCompleted, siteLoadingError, roofSquareTableData]);

    /** Parent Design / module step → highlight roofs + module include state in this embed. */
    useEffect(() => {
        if (!isViewportOnlyEmbed) return undefined;

        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as {
                type?: string;
                includedRoofTagIds?: number[];
                includedModuleIds?: string[];
                ghostedTreeIds?: string[];
                vendor?: string;
                jobs?: PciSimulateOptionJob[];
                orientations?: Array<'portrait' | 'landscape' | 'both'>;
                fills?: Record<string, unknown>;
                modulesLayout?: SavedRoofModulesLayout | null;
                fillLayout?: SavedRoofModulesLayout | null;
            } | null;
            if (!data?.type) return;
            const viewer = useViewerStore.getState().sceneViewer;
            if (data.type === PCI_ONEDRAW_APPLY_DESIGN_MSG) {
                const includedModuleIds = Array.isArray(data.includedModuleIds)
                    ? data.includedModuleIds
                    : [];
                const ghostedTreeIds = Array.isArray(data.ghostedTreeIds) ? data.ghostedTreeIds : [];
                pciModuleSelectionRef.current =
                    includedModuleIds.length > 0 ? includedModuleIds : null;
                pciTreeGhostRef.current = ghostedTreeIds;
                void applyPciDesignOptionToViewer(viewer, {
                    includedModuleIds,
                    modulesLayout: data.modulesLayout,
                    ghostedTreeIds,
                    fillLayout: data.fillLayout,
                })
                    .then((ok) => {
                        try {
                            window.parent.postMessage(
                                { type: PCI_ONEDRAW_APPLY_DESIGN_DONE_MSG, ok },
                                window.location.origin,
                            );
                        } catch {
                            /* ignore */
                        }
                    })
                    .catch(() => {
                        try {
                            window.parent.postMessage(
                                { type: PCI_ONEDRAW_APPLY_DESIGN_DONE_MSG, ok: false },
                                window.location.origin,
                            );
                        } catch {
                            /* ignore */
                        }
                    });
                return;
            }
            if (data.type === PCI_ONEDRAW_CAPTURE_MODULES_MSG) {
                const ids = Array.isArray(data.includedModuleIds) ? data.includedModuleIds : [];
                const layout = captureIncludedModulesLayout(viewer, ids);
                try {
                    window.parent.postMessage(
                        { type: PCI_ONEDRAW_MODULES_LAYOUT_MSG, layout },
                        window.location.origin,
                    );
                } catch {
                    /* ignore */
                }
                return;
            }
            if (data.type === PCI_ONEDRAW_MAXFILL_MSG) {
                const orientations = Array.isArray(data.orientations) ? data.orientations : [];
                void (async () => {
                    try {
                        const snapshots =
                            orientations.length > 0
                                ? await capturePciMaxfillStrategies(viewer, orientations)
                                : [await capturePciMaxfillSnapshot(viewer)].filter(
                                      (row): row is NonNullable<typeof row> => row != null,
                                  );
                        window.parent.postMessage(
                            {
                                type: PCI_ONEDRAW_MAXFILL_RESULT_MSG,
                                snapshots,
                                snapshot: snapshots[0] ?? null,
                            },
                            window.location.origin,
                        );
                    } catch {
                        try {
                            window.parent.postMessage(
                                {
                                    type: PCI_ONEDRAW_MAXFILL_RESULT_MSG,
                                    snapshots: [],
                                    snapshot: null,
                                },
                                window.location.origin,
                            );
                        } catch {
                            /* ignore */
                        }
                    }
                })();
                return;
            }
            if (data.type === PCI_ONEDRAW_SIMULATE_OPTIONS_MSG) {
                const jobs = Array.isArray(data.jobs) ? data.jobs : [];
                const fills = data.fills && typeof data.fills === 'object' ? data.fills : {};
                const ghostedTreeIds = Array.isArray(data.ghostedTreeIds) ? data.ghostedTreeIds : undefined;
                const previousGhostRef = pciTreeGhostRef.current;
                if (ghostedTreeIds) pciTreeGhostRef.current = ghostedTreeIds;
                void simulatePciOptionsInViewer(viewer, jobs, fills as never, ghostedTreeIds)
                    .then((results) => {
                        window.parent.postMessage(
                            { type: PCI_ONEDRAW_SIMULATE_RESULTS_MSG, results },
                            window.location.origin,
                        );
                    })
                    .catch(() => {
                        try {
                            window.parent.postMessage(
                                { type: PCI_ONEDRAW_SIMULATE_RESULTS_MSG, results: [] },
                                window.location.origin,
                            );
                        } catch {
                            /* ignore */
                        }
                    })
                    .finally(() => {
                        pciTreeGhostRef.current = previousGhostRef;
                    });
                return;
            }
            if (data.type === PCI_ONEDRAW_ROOF_SELECTION_MSG) {
                const ids = Array.isArray(data.includedRoofTagIds) ? data.includedRoofTagIds : [];
                pciRoofSelectionRef.current = ids;
                applyPciRoofSelectionHighlight(viewer, ids);
                return;
            }
            if (data.type === PCI_ONEDRAW_MODULE_SELECTION_MSG) {
                const ids = Array.isArray(data.includedModuleIds) ? data.includedModuleIds : [];
                pciModuleSelectionRef.current = ids;
                applyPciModuleSelectionHighlight(viewer, ids);
                return;
            }
            if (data.type === PCI_ONEDRAW_3D_VIEW_MSG) {
                preparePci3dView(viewer);
                if (pciModuleSelectionRef.current) {
                    applyPciModuleSelectionHighlight(viewer, pciModuleSelectionRef.current);
                }
                return;
            }
            if (data.type === PCI_ONEDRAW_TREE_GHOST_MSG) {
                const ids = Array.isArray(data.ghostedTreeIds) ? data.ghostedTreeIds : [];
                pciTreeGhostRef.current = ids;
                const changed = applyPciTreeGhostSelection(viewer, ids);
                if (changed) {
                    try {
                        postPciSiteRoofsToParent(viewer);
                    } catch {
                        /* ignore */
                    }
                }
                return;
            }
            if (data.type === PCI_ONEDRAW_LOSSES_VENDOR_MSG) {
                const vendor = typeof data.vendor === 'string' ? data.vendor : 'oneDRAW';
                applyPciLossesVendor(viewer, vendor);
                return;
            }
            if (data.type === PCI_ONEDRAW_VIEWPORT_RESIZE_MSG) {
                window.dispatchEvent(new Event('resize'));
                try {
                    viewer?.onWindowResize?.();
                } catch {
                    /* ignore */
                }
                // Dock size often settles after first paint — keep framing the building briefly.
                if (pciCameraFittedRef.current && pciResizeFitCountRef.current < 3) {
                    fitPciCameraToBuilding(viewer, 'top');
                    pciResizeFitCountRef.current += 1;
                }
                // Selection/materials can look stale after canvas resize — re-apply.
                if (pciRoofSelectionRef.current) {
                    applyPciRoofSelectionHighlight(viewer, pciRoofSelectionRef.current);
                }
                if (pciModuleSelectionRef.current) {
                    applyPciModuleSelectionHighlight(viewer, pciModuleSelectionRef.current);
                }
                if (pciTreeGhostRef.current) {
                    applyPciTreeGhostSelection(viewer, pciTreeGhostRef.current, {
                        refreshShade: false,
                    });
                }
            }
        };

        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [isViewportOnlyEmbed]);

    /** Apply the pre-contract AI option selected via Open in OneDraw. */
    useEffect(() => {
        if (isViewportOnlyEmbed || !sceneViewer || !loadingCompleted) return undefined;
        if (!peekPreContractOpenDesign()) return undefined;

        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 40;

        let timer: number | undefined;

        const applyPending = async () => {
            if (cancelled) return true;
            const payload = peekPreContractOpenDesign();
            if (!payload?.includedModuleIds.length) return true;

            const hasSnapshot =
                Array.isArray(payload.modulesLayout) && payload.modulesLayout.length > 0;
            const roofsReady = (sceneViewer.roofMeshArr?.length ?? 0) > 0;
            const modulesReady = (sceneViewer.moduleMeshArr?.length ?? 0) > 0;
            const waitingForModel = hasSnapshot ? !roofsReady : !modulesReady;
            const ghostedTreeIds = payload.ghostedTreeIds ?? [];
            const waitingForTrees =
                ghostedTreeIds.length > 0 &&
                (sceneViewer.treeGroup?.children?.length ?? 0) === 0;
            if ((waitingForModel || waitingForTrees) && attempts < maxAttempts) {
                attempts += 1;
                return false;
            }

            try {
                const applied = await applyPciIncludedModulesToViewer(
                    sceneViewer,
                    payload.includedModuleIds,
                    payload.modulesLayout,
                );
                if (!applied && attempts < maxAttempts) {
                    attempts += 1;
                    return false;
                }
                if (applied && payload.includedRoofTagIds.length) {
                    applyPciRoofSelectionHighlight(sceneViewer, payload.includedRoofTagIds);
                }
                if (applied && ghostedTreeIds.length) {
                    applyPciTreeGhostSelection(sceneViewer, ghostedTreeIds, {
                        refreshShade: true,
                    });
                }
            } catch {
                /* still consume so a failed apply doesn't retry forever */
            }
            consumePreContractOpenDesign();
            if (peekPreContractAutoFinalize()) {
                window.dispatchEvent(new CustomEvent(PCI_PRECONTRACT_READY_TO_FINALIZE));
            }
            return true;
        };

        void applyPending().then((done) => {
            if (done || cancelled) return;
            timer = window.setInterval(() => {
                void applyPending().then((finished) => {
                    if (finished && timer != null) window.clearInterval(timer);
                });
            }, 300);
        });

        return () => {
            cancelled = true;
            if (timer != null) window.clearInterval(timer);
        };
    }, [isViewportOnlyEmbed, sceneViewer, loadingCompleted]);


    const keyDownListener = (e: KeyboardEvent) => {

        const isModalOpenInInterface = useViewerStore.getState().isModalOpenInInterface;
        const isTypeEvent = useViewerStore.getState().isTypeEvent;


        if (isTypeEvent) return;

        if (!pathname.includes('configure') || isModalOpenInInterface) {
            return;
        }

        if (e.ctrlKey) {
            if (hasCapability('Undo') && e.key === 'z') {
                // Undo: Ctrl + Z
                viewerInstance.undo();
            } else if (hasCapability('Redo') && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                // Redo: Ctrl + Y or Ctrl + Shift + Z
                viewerInstance.redo();
            }
        } else if (e.altKey) {
            if (e.key === 't') {
                //Alt + T
                viewerInstance.setupSelectionBoxHelper(SelectType.tree);
            }
        } else if (e.shiftKey) {
            if (hasCapability('Orbit') && e.key.toLowerCase() === 'o') {
                //Shift + O
                viewerInstance.setOrbitControlsMode(EditorControlMode.orbit);
            } else if (hasCapability('Pan') && e.key.toLowerCase() === 'p') {
                //Shift + P
                viewerInstance.setOrbitControlsMode(EditorControlMode.pan);
            } else if (e.key.toLowerCase() === 'h') {
                //Shift + H
                clickButtonWithRef("defaultPositionBtn")
            } else if (e.key === 'Delete') {
                //Shift + Delete
                clickButtonWithRef("clearSceneBtn")
            }
        } else if (e.key === 'Escape') {
            useViewerStore.setState({selRoofFaceArr: []});
            sceneViewer.removeSelectionBoxHelper();
            //if pathname is not tape-measure
            if (viewerInstance.pushMode) {
                return viewerInstance.setPushMode(false);
            }
            if (getPathnameIncludes('tape-measure')) {
                viewerInstance.resetDraw();
                viewerInstance.setDrawMode(false);
                viewerInstance.setAutoAlignMode(false);
                setPathname('/configure/roof');
            } else {
                viewerInstance.setDrawMode(false);
                viewerInstance.setAutoAlignMode(false);
            }
            const event = new Event("closeContextMenu");
            document.getElementById("canvasContainer")?.dispatchEvent(event);
            viewerInstance.deselectAllSelectedObjectsAndEndTask();
        } else if (e.key === 'Delete') {
            viewerInstance.setModuleSetting('delete');
        } else if (e.key?.toLowerCase() === 'p') {
            //P - Push Pull
            clickButtonWithRef("pushPullToolBtn")
        } else if (hasCapability('Tape_Measure') && e.key.toLowerCase() === 'd') {
            //Q - Measure
            clickButtonWithRef("btnMeasure")
        } else if (hasCapability('X-Ray_View') && e.key.toLowerCase() === 'k') {
            //K - X Ray
            clickButtonWithRef("btnXRay")
        } else {
            // Switch Menu Shortcuts
            if (
                hasCapability('Show_AI_Scenarios') &&
                e.key.toLowerCase() === 'a' &&
                !useViewerStore.getState().isDashboardOpen
            ) {
                //A - AI designs side pane (viewer only — not when full dashboard is open)
                triggerV3AiDesignsPanel();
            } else if (hasCapability('What_If') && e.key.toLowerCase() === 'w') {
                //W - What If
                sceneViewer.setMenu(TabName.WhatIf);
            } else if (e.key.toLowerCase() === 'o') {
                //O -Obstruction
                sceneViewer.setMenu(TabName.Obstruction);
            } else if (e.key.toLowerCase() === 'r') {
                //R- Roof
                sceneViewer.setMenu(TabName.Roof);
            } else if (e.key.toLowerCase() === 't') {
                //T- Tree
                sceneViewer.setMenu(TabName.Tree);
            } else if (e.key.toLowerCase() === 'm') {
                //M - Module
                sceneViewer.setMenu(TabName.Modules);
            } else if (e.key.toLowerCase() === 'i' && hasCapability("Irradiance")) {
                //I - Irradiance
                sceneViewer.setMenu(TabName.Irradiance);
            } else if (e.key.toLowerCase() === 's') {
                //S - Solar Access
                sceneViewer.setMenu(TabName.SolarAccess);
            } else if (e.key.toLowerCase() === 'l' && hasCapability('Losses_Vendors')) {
                //L - Losses
                sceneViewer.setMenu(TabName.Losses);
            } else if (e.key.toLowerCase() === 'e' && hasCapability('EPC_Calculations')) {
                //E - EPC
                sceneViewer.setMenu(TabName.EPC);
            } else if (e.key.toLowerCase() === 'f' && (hasCapability('Surface Draw') || hasCapability('Surface_Draw'))) {
                //F - Ground Mount
                sceneViewer.setMenu(TabName.SurfaceDraw);
            } else if (e.key.toLowerCase() === 'b' && hasCapability("View_Building_Attributes")) {
                //B - Building
                sceneViewer.setMenu(TabName.Building);
            } else if (e.key.toLowerCase() === 'x' && hasCapability("Layers")) {
                //X - X-Ray Toggle for Trees
                const prevState = useViewerStore.getState().layerVisibilityState[LAYER_OPTIONS.MODEL.TREE]
                toggleLayer(LAYER_OPTIONS.MODEL.TREE, !prevState);
                viewerInstance.setVisibleGroup(LAYER_OPTIONS.MODEL.TREE, !prevState);
            }

        }
    };

    useEffect(() => {
        try {
            masterDataService
                .getMasterDataList()
                .then(async (masterDataAPIResponse) => {
                    useViewerStore.setState({masterDataList: masterDataAPIResponse});
                })
                .catch((error) => {
                    logger.error(error);
                });
        } catch (e) {
            logger.error(e);
        }
    }, []);

    useEffect(() => {
        const loadingContext = initialLoadLoadingContextRef;
        let webglListenerAttached = false;

        try {
            viewerInstance = new Viewer();
        } catch (e) {
            logger.error(
                'WebGL context could not be created (too many GPU tabs, disabled hardware acceleration, or driver issue). Close other 3D apps/tabs and reload.',
                e,
            );
            useViewerStore.setState({ sceneViewer: null });
            return () => {};
        }

        useViewerStore.setState({
            sceneViewer: viewerInstance,
            loadingCompleted: false,
            siteLoadingError: null,
        });
        setPathname('/configure/roof');
        viewerInstance.selectType = SelectType.roof;

        const handleWebGLContextLost = () => {
            if ((useViewerStore.getState() as any).treeReprocessInProgress) {
                useViewerStore.setState({
                    aiModuleFillingError: '3D view was reset during processing. Please refresh the page to continue.',
                });
                return;
            }
        };

        if (loadingContext) {
            viewerInstance.renderer.domElement.addEventListener('webglcontextlost', handleWebGLContextLost, false);
            webglListenerAttached = true;
        }

        let canvasResizeObserver: ResizeObserver | null = null;
        let rafResizeId: number | null = null;
        const scheduleCanvasResize = () => {
            if (rafResizeId != null) return;
            rafResizeId = window.requestAnimationFrame(() => {
                rafResizeId = null;
                viewerInstance.resizeCanvas();
            });
        };

        void (async () => {
            if (!loadingContext || !user?.user_Id) {
                return;
            }

            const urlParams = new URLSearchParams(window.location.search);
            const shouldAbortBatch = (): boolean => {
                if (urlParams.get('batch') !== 'true') return false;
                try {
                    if (sessionStorage.getItem('batchStopRequested') === 'true') return true;
                } catch {
                    // ignore
                }
                const bState = loadBatchState();
                return Boolean(bState && !bState.isProcessing);
            };
            const stopWatchdog =
                urlParams.get('batch') === 'true'
                    ? window.setInterval(() => {
                          if (shouldAbortBatch()) {
                              // Hard-abort: navigating away cancels fetches/promises.
                              navigate('/batch-ai');
                          }
                      }, 250)
                    : null;

            try {
                const sfid = urlParams.get('sfid');

                if (!sfid || sfid === '' || sfid === null) {
                    useOneButtonStore.getState().setSalesForceId(null);
                    reportSiteLoadError(
                        'No site was found. Add a valid Salesforce ID to the URL (sfid=…) and reload.',
                    );
                    return;
                }

                // Clear stale SF id so dashboard opp-init does not run until getOpportunity succeeds.
                useOneButtonStore.getState().setSalesForceId(null);

                if (shouldAbortBatch()) {
                    navigate('/batch-ai');
                    return;
                }

                loadingContext.setMessage({message: 'Getting Site Data...'});

                setShowEnergyCommunityNotice(false);
                let opportunity;
                try {
                    opportunity = await siteService.getOpportunity(sfid);
                } catch (opportunityError) {
                    const message = formatSiteLoadErrorMessage(
                        opportunityError,
                        OPPORTUNITY_NOT_AVAILABLE_MESSAGE,
                    );
                    if (!hasCapability('Show_OneButton')) {
                        logger.error(opportunityError);
                        useViewerStore.setState({
                            loadingCompleted: true,
                            refreshingShade: false,
                            siteLoadingError: message,
                            isDashboardOpen: false,
                        });
                        if (useViewerStore.getState().isViewportOnlyEmbed) {
                            postPciSiteLoadErrorToParent(message);
                        } else {
                            viewerInstance.viewerAlert({
                                show: true,
                                title: 'Opportunity Data Not Available',
                                message,
                                messageType: AlertType.error,
                                isModal: true,
                            });
                        }
                        return;
                    }
                    throw opportunityError;
                }
                useOneButtonStore.getState().setSalesForceId(sfid);
                const response = await siteService.postSitesSearch(sfid, 1, [], {}, false, false);
                const isLikelyQualifies = opportunityLikelyQualifiesAsEnergyCommunity(opportunity);
                setShowEnergyCommunityNotice(isLikelyQualifies);

                if (shouldAbortBatch()) {
                    navigate('/batch-ai');
                    return;
                }

                if (response.Message === "No records found") {
                    throw new Error("No Site Found for the given SalesForceId, Please check the SalesForceId and try again.");
                }

                const site = response.Data[0];
                useViewerStore.setState({productType: site.ProductCategory});
                useViewerStore.setState({viewerAlert: { show: false, message: '' }});
                useViewerStore.setState({
                    useJune2026SquareCalculation: new Date(opportunity.CreatedDate) >= new Date("2026-06-15"),
                });

                loadingContext.setMessage({message: 'Processing Site Data...'});
                const [state, zipCode] = site.State.split("-");
                const stateCode = getStateCodeFromStateName(state);

                // Formatting the address
                let address = site.Address;
                if (site.Address?.includes(",")) {
                    const addressFirstPartTrimmed = site.Address.split(",")[0];
                    const fullAddress = `${addressFirstPartTrimmed}, ${site.City}, ${stateCode}`;
                    address = `${fullAddress.split(",")[0]}, ${fullAddress.split(",")[1]}, ${fullAddress.split(",")[2].split("-")[0]}`;
                }

                document.title = `${address} - OneDraw`;

                const stage: string = site.Stage?.split(':')[0];

                // Warning if Stage 4 or later and user is not Admin/Sales Op/RSA
                const canModifyStage4 = hasCapability('Stage_4_Post_Contract_Finalize');
                const canFinalize = hasCapability('Finalize');
                if (canFinalize && (stage && Number(stage) >= 4) && !canModifyStage4) {
                    alert("Alert: This opportunity is in stage 4 or later, therefore no modification will be saved");
                }

                const isUserHaveSetbackOverrideCapability = hasCapability("Add_Panels_Past_Setbacks");

                if(isUserHaveSetbackOverrideCapability){
                    useViewerStore.setState({isUserHaveCapabilityToAddPanelsPastSetbacks : true});
                }
                else{
                    useViewerStore.setState({isUserHaveCapabilityToAddPanelsPastSetbacks : false});
                }

                sessionStorage.setItem('OppurtunityId', site.Account_Id);
                sessionStorage.setItem('SalesForceId', site.SalesForceId);
                sessionStorage.setItem('stateCode', stateCode);
                sessionStorage.setItem('postalCode', zipCode);
                sessionStorage.setItem('directLead', opportunity.DirectLeadSfId);
                sessionStorage.setItem('customerNumber', opportunity.CustomerNumber ?? '');
                sessionStorage.setItem('opportunityName', site.Opportunity_Name);
                sessionStorage.setItem('city', site.City);
                sessionStorage.setItem('currentStage', stage);
                sessionStorage.setItem('stageStatus', site.Stage_Status);
                sessionStorage.setItem('Utility_Company', site.Utility_Company);
                sessionStorage.setItem('Multiplier', site.Multiplier);

                useViewerStore.setState({ siteAddress: address, loadingCompleted: false, siteLoadingError: null });
                loadingContext.setMessage({message: 'Fetching Modules...'});

                const moduleListAPIResponse = await moduleServices.getModuleList(stateCode);
                if (shouldAbortBatch()) {
                    navigate('/batch-ai');
                    return;
                }
                if(!moduleListAPIResponse.Modules && moduleListAPIResponse.response.status !== 200) {
                    throw new Error(moduleListAPIResponse.Message || `Failed to fetch Module List, API Error : Status Code ${moduleListAPIResponse.response.status}`);

                }
                const moduleList = moduleListAPIResponse.Modules.map((item: ModuleListAPIResponseItem, index: number) => ({
                    key: index,
                    text: item.Name,
                    value: item.Name,
                    power: item.Wattage,
                    w: item.WidthInMm / 1000,
                    h: item.LengthInMm / 1000,
                    manufacturer: item.Manufacturer,
                }));

                const defaultModule = moduleList.find((module: ModuleTypeOptionData) => module.text === moduleListAPIResponse.DefaultModule.Name);

                useViewerStore.setState({moduleList});
                useViewerStore.setState({activeModule: defaultModule});

                loadingContext.setMessage({message: 'Pulling site design...'});
                const token = await siteService.getOneDrawEntraIdToken();
                if (shouldAbortBatch()) {
                    navigate('/batch-ai');
                    return;
                }
                const siteDesigns = await siteService.loadSiteDesigns(site.Account_Id, token);
                if (shouldAbortBatch()) {
                    navigate('/batch-ai');
                    return;
                }

                if (
                    !siteDesigns?.aurora &&
                    !siteDesigns?.eagleView &&
                    !hasCapability('Show_OneButton')
                ) {
                    useViewerStore.setState({
                        loadingCompleted: true,
                        refreshingShade: false,
                        siteLoadingError: DEFAULT_SITE_VIEWER_MESSAGE,
                        isDashboardOpen: false,
                    });
                    if (useViewerStore.getState().isViewportOnlyEmbed) {
                        postPciSiteLoadErrorToParent(DEFAULT_SITE_VIEWER_MESSAGE);
                    } else {
                        viewerInstance.viewerAlert({
                            show: true,
                            title: SITE_VIEWER_UNAVAILABLE_TITLE,
                            message: DEFAULT_SITE_VIEWER_MESSAGE,
                            messageType: AlertType.error,
                            isModal: true,
                        });
                    }
                    return;
                }

                useViewerStore.setState({designList : []});

                loadingContext.setMessage({message: 'Getting Site Designs...'});

                const designList = await getDesignVersionList(sfid);
                if (shouldAbortBatch()) {
                    navigate('/batch-ai');
                    return;
                }

                packingSlipService.fetchFinancePartnerDetails().then(details => {
                    useViewerStore.setState({ financePartnerDetails: details });
                });

                if (designList === null) {
                    setIsDesignFetching(false);
                    useViewerStore.setState({accessoryBuildingList: []});
                    await getSite(navigate)
                    return;
                }

                // In batch mode, load the specific design/version from the batch queue
                let design = designList[0];
                applySunPositionVersionFromDesign(design);
                let selectedIdx = 0;
                const batchDesignNo = sessionStorage.getItem('batchDesignNo');
                const batchVersionNo = sessionStorage.getItem('batchVersionNo');
                if (urlParams.get('batch') === 'true' && batchDesignNo && batchVersionNo) {
                    const matchIdx = designList.findIndex(
                        (d: any) => String(d.Design_No) === batchDesignNo && String(d.Version_No) === batchVersionNo
                    );
                    if (matchIdx >= 0) {
                        design = designList[matchIdx];
                        selectedIdx = matchIdx;
                    }
                }

                useViewerStore.setState({
                    selectedDesignIndex: selectedIdx, 
                    designList : designList,
                });

                
                setIsDesignFetching(false);
                const data = await siteService.retrieveSiteDesignDetailsWithDesignData(sfid, design.Design_No, design.Version_No);
                if (shouldAbortBatch()) {
                    navigate('/batch-ai');
                    return;
                }

                const designData = data.DesignDataList[0].Design_Data;
                useViewerStore.setState({designID: data.DesignDataList[0].DesignID});
                const siteDetails = useViewerStore.getState().siteDesignDetails;
                const siteEnvelope =
                    Array.isArray(siteDetails) && siteDetails.length > 0 ? siteDetails[0] : undefined;
                useViewerStore.setState({
                    customerSiteRestrictions: pickCustomerSiteRestrictions(
                        data,
                        data.DesignDataList?.[0],
                        design,
                        siteEnvelope,
                    ),
                });

                const accessoryBuildingList = designList[0].Buildings.filter((building: BuildingDesignData) => building.Principal_Structure === false).map((building: BuildingDesignData) => Number(building.Building_Name));
                useViewerStore.setState({accessoryBuildingList: accessoryBuildingList});

                setDeactivateRoofArrInStoreFromDesignData(designList,0)

                const openCanvasFirst = shouldOpenCanvasOnConfigureLoad();
                if (
                    hasCapability('Show_OneButton') &&
                    urlParams.get('batch') !== 'true' &&
                    !openCanvasFirst
                ) {
                    // Dashboard-first: keep opportunity APIs only. Defer oneDRAW draw + shade
                    // until the user opens the 3D viewer (see openSiteViewer3dFromDashboard).
                    useViewerStore.setState({
                        loadingCompleted: true,
                        deferredSiteModelLoad: { designData, sfid, modelType: 'glb' },
                        siteModelLoadPending: true,
                    });
                    deferSiteViewerCalculationsOnLoad();
                    return;
                }

                if (openCanvasFirst) {
                    useViewerStore.setState({ isDashboardOpen: false });
                }

                loadingContext.setMessage({message: 'Fetching Setback Info...'});
                await fetchSetbackInfo(sfid, sceneViewer);
                if (shouldAbortBatch()) {
                    navigate('/batch-ai');
                    return;
                }


                checkAndSetSetbackOverrideFlag(designData)

                loadingContext.setToBackground(true);
                loadingContext.setMessage({title: '', message: 'Drawing Site...'});

                await LoadModelJson(viewerInstance, designData, 'glb');
                if (shouldAbortBatch()) {
                    navigate('/batch-ai');
                    return;
                }


                // Ensure tree label visibility in the viewer matches the store flag after load
                {
                    const wantLabels = useViewerStore.getState().isTreeLabelVisible;
                    const currentVisible = viewerInstance.treeTagGroup?.visible;
                    if (!!currentVisible !== wantLabels) {
                        viewerInstance.toggleTreeLabel();
                    }
                }
            } catch (e: unknown) {
                logger.error(e);
                const message = formatSiteLoadErrorMessage(e);

                if (urlParams.get('batch') !== 'true') {
                    const failedSfid = urlParams.get('sfid')?.trim();
                    if (failedSfid) {
                        useOneButtonStore.getState().setSalesForceId(failedSfid);
                    }
                }

                // In batch mode, record the loading error and advance to the next site
                if (urlParams.get('batch') === 'true') {
                    const bState = loadBatchState();
                    if (bState && bState.currentIndex < bState.records.length) {
                    if (!bState.isProcessing) {
                        navigate('/batch-ai');
                        return;
                    }
                    const processedIndex = bState.currentIndex;
                        const startTime = Number(sessionStorage.getItem('batchSiteStartTime') || Date.now());
                        bState.results[bState.currentIndex] = {
                            ...bState.results[bState.currentIndex],
                            status: 'error',
                            error: `Site loading failed: ${message}`,
                            processingTimeMs: Date.now() - startTime,
                        };
                        bState.currentIndex += 1;

                    // Optional: stop after processing a single row (used by Batch AI "Re-run this site").
                    const stopAfterIndexRaw = sessionStorage.getItem('batchStopAfterIndex');
                    const stopAfterIndex = stopAfterIndexRaw != null ? Number(stopAfterIndexRaw) : null;
                    if (
                        stopAfterIndex != null &&
                        !Number.isNaN(stopAfterIndex) &&
                        processedIndex === stopAfterIndex
                    ) {
                        bState.isProcessing = false;
                        bState.completedAt = Date.now();
                        try { sessionStorage.removeItem('batchStopAfterIndex'); } catch { /* ignore */ }
                        saveBatchState(bState);
                        navigate('/batch-ai');
                        return;
                    }

                        if (bState.currentIndex >= bState.records.length) {
                            bState.isProcessing = false;
                            bState.completedAt = Date.now();
                            saveBatchState(bState);
                            navigate('/batch-ai');
                        } else {
                            const nextRec = bState.records[bState.currentIndex];
                            bState.results[bState.currentIndex] = { ...bState.results[bState.currentIndex], status: 'processing' };
                            saveBatchState(bState);
                            navigateToNextBatchSite(nextRec);
                        }
                        return;
                    }
                }

                reportSiteLoadError(message);
            } finally {
                if (stopWatchdog != null) {
                    window.clearInterval(stopWatchdog);
                }
                if (loadingContext) {
                    loadingContext.dispose();
                }
                if (loadingContext && user?.user_Id) {
                    window.addEventListener('keydown', keyDownListener, false);
                    window.addEventListener('resize', scheduleCanvasResize);
                    window.addEventListener('orientationchange', scheduleCanvasResize);
                    const canvasContainer = document.getElementById('canvasContainer');
                    if (canvasContainer && 'ResizeObserver' in window) {
                        canvasResizeObserver = new ResizeObserver(() => {
                            scheduleCanvasResize();
                        });
                        canvasResizeObserver.observe(canvasContainer);
                    }
                    scheduleCanvasResize();
                }
            }
        })();

        return () => {
            if (webglListenerAttached) {
                try {
                    viewerInstance.renderer.domElement.removeEventListener('webglcontextlost', handleWebGLContextLost, false);
                } catch {
                    // ignore
                }
            }
            window.removeEventListener('keydown', keyDownListener, false);
            window.removeEventListener('resize', scheduleCanvasResize);
            window.removeEventListener('orientationchange', scheduleCanvasResize);
            canvasResizeObserver?.disconnect();
            if (rafResizeId != null) {
                window.cancelAnimationFrame(rafResizeId);
                rafResizeId = null;
            }
            viewerInstance.dispose?.();
            useViewerStore.setState({ sceneViewer: null });
            sessionStorage.setItem('OppurtunityId', "");
            sessionStorage.setItem('SalesForceId', "");
            sessionStorage.setItem('stateCode', "");
            sessionStorage.setItem('postalCode', "");
            sessionStorage.setItem('directLead', "");
            sessionStorage.setItem('opportunityName', "");
            sessionStorage.setItem('currentStage', "");
            sessionStorage.setItem('stageStatus', "");
            useViewerStore.setState({siteAddress: ''});
        };
    }, [user, initialLoadLoadingContextRef, navigateToNextBatchSite, reportSiteLoadError]);

    useEffect(() => {
        setPathname(pathname);
        // Clear multiplier flags before enableAllLayers / setVisibleGroup (they read this store).
        if (pathname.includes('price-calculation')) {
            viewerInstance.toggleTreeMultiplierLabel(true);
            useViewerStore.setState({
                isTreeMultiplierLabelVisible: true,
            });
        } else {
            viewerInstance.toggleTreeMultiplierLabel(false);
            viewerInstance.toggleModuleMultiplierLabel(false);
            useViewerStore.setState({
                isTreeMultiplierLabelVisible: false,
                isModuleMultiplierLabelVisible: false,
            });
        }

        viewerInstance.enableAllLayers(pathname.includes('modules'));
        let selectType = viewerInstance.getSelectTypeFromPath(pathname)
        const measureMode = pathname.includes('measure');
        viewerInstance.hideObject(HideObjectType.GhostObst, true);
        if (!pathname.includes('roof')) {
            viewerInstance.toggleRoofTag(false);
            useViewerStore.setState({
                isRoofTagVisible: false, isMoveRoofActive: false
            });
        }

        if (!pathname.includes('tree')) {
            useViewerStore.setState({
                isHideAllSelectedTrees: false,
                isTreePerimeterToolActive: false,
                isMoveTreeActive: false,
                isAddTreeActive: false
            });
            viewerInstance.hideObject(HideObjectType.Seltree, false);
            viewerInstance.showRoofSquirrel(false);
        }

        if (pathname.includes('modules')) {
            viewerInstance.setVisibleGroup(LAYER_OPTIONS.MODEL.TREE, false);
            viewerInstance.hideObject(HideObjectType.GhostTree, true);
        } else if (layerVisibilityState.tree) {
            viewerInstance.setVisibleGroup(LAYER_OPTIONS.MODEL.TREE, true);
        }

        if (!pathname.includes('obstruction')) {
            useViewerStore.setState({
                isHideAllGhostObstructions: true, isMoveObstructionActive: false, isAddObstructionActive: false
            });

        }

        if (pathname.includes('obstruction')) {
            viewerInstance.setVisibleGroup(LAYER_OPTIONS.MODEL.TREE, false);
            useViewerStore.setState({
                isHideAllGhostObstructions: true
            });

        }


        viewerInstance.setSelectType(selectType);
        viewerInstance.setMeasureMode(measureMode);
        viewerInstance.setAutoAlignMode(false);
        if(isHideAllGhostTrees) viewerInstance.hideObject(HideObjectType.GhostTree, isHideAllGhostTrees);
        useViewerStore.setState({isTapandHold: false})
        useViewerStore.setState({isTabandHoldUp: false});
    }, [pathname])

    useEffect(() => {
        if (!loadingCompleted) {
            return;
        }

        if (selectedDesignIndex >= 0 && designList[0]?.Design_Certification_Status && designList[0]?.Design_Certification_Status !== "Not Certified" && designList[0]?.Design_Certification_Status !== "Certified Design Override" && designList[0]?.Design_Certification_Status !== null) {
            const isUnlockedSite = hasCapability("Pre_Installed_Certified") || hasCapability("Install_Change_Certified") || hasCapability("As_Built_Certified") || hasCapability("Certified_Design_Override")
            if (!isUnlockedSite) {
                setTimeout(() => {
                    sceneViewer?.viewerAlert({
                        show: true,
                        title: <span style={{ display: "flex" }}><IoIosLock
                            size={25} /> {designList[0]?.Design_Certification_Status}</span>,
                        message: <LockedDesignValidation
                            certification_status={designList[0]?.Design_Certification_Status} />,
                        messageType: AlertType.warning,
                        isModal: true
                    })
                }, 5000);
            }
        }
        if (isDesignLoading) {
            dismissLightReachPitchWarningIfShowing();
            return;
        }

        syncLightReachSitePitchWarning(sceneViewer);
    }, [loadingCompleted, isDesignLoading, financePartnerDetails, roofPitchOverrideArr, selectedDesignIndex, sceneViewer, designList, roofInfoArr])

    useEffect(() => {
        viewerInstance.hideObject(HideObjectType.GhostObst, true);
        useViewerStore.setState({isHideAllGhostObstructions: true});
    }, [isGhostObstructionUpdated]);

    useEffect(() => {
        if (isDesignFetching) return;
        const selectedDesign = designList[selectedDesignIndex];
        const depKey = [
            selectedDesignIndex,
            isNonFinalizedSavedDesign ? 1 : 0,
            buildingWise33PercentRuleModuleCountList.length,
            accessoryBuildingList.length,
        ].join('|');
        if (roofSquarePopulateKeyRef.current === depKey) return;
        roofSquarePopulateKeyRef.current = depKey;
        void populateRoofSquareData(selectedDesign);
    }, [buildingWise33PercentRuleModuleCountList, selectedDesignIndex, isNonFinalizedSavedDesign, accessoryBuildingList]);

    return (
        <>
         <div ref={canvasContainerRef} id={'canvasContainer'}>
            {isDebugModeEnabled && !isViewportOnlyEmbed ? <OneDrawDebuggerGUI /> : null}
        </div>
        {showEnergyCommunityNotice && loadingCompleted && (isViewportOnlyEmbed || !isDashboardOpen) ? (
            <EnergyCommunityNotice onClose={() => setShowEnergyCommunityNotice(false)} />
        ) : null}
        <ViewerLoadingOverlays />
        {!isViewportOnlyEmbed ? <EditorDashboard /> : null}
        </>
       
    );
}

export default ThreeDViewer;